import type { QueryRef } from '@apollo/client';
import { useBackgroundQuery, useReadQuery } from '@apollo/client';
import type { FC } from 'react';
import { Suspense } from 'react';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { MetaData } from '@xing-com/crate-entity-pages-common';
import { useEditContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';

import * as Styled from './app.styles';
import { AboutUsErrorPage } from './components/about-us-error-page/about-us-error-page';
import { Affiliates } from './components/affiliates-container/affiliates';
import { DetailedDescription } from './components/detailed-description/detailed-description';
import { DocumentsContainer } from './components/documents/documents-container';
import { FactsContainer } from './components/facts/facts-container';
import { GalleryPreviewContainer } from './components/gallery-preview/gallery-preview-container';
import { AboutUsSkeleton } from './components/skeleton/skeleton';
import { DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS_SUBPAGE } from './config/media';
import type { AboutUsSubpageQuery } from './graphql/queries/about-us-subpage-query.gql-types';
import { AboutUsSubpageDocument } from './graphql/queries/about-us-subpage-query.gql-types';

const AppInner: FC<{ query: QueryRef<AboutUsSubpageQuery | undefined> }> = ({
  query,
}) => {
  const { isEditing } = useEditContext();
  const { pageContext } = usePageContext();
  const { data, error } = useReadQuery(query);

  if (error || !data) throw error;

  const pagesAboutUs =
    data.pagesAboutUs?.__typename === 'AboutEntity' ? data.pagesAboutUs : null;
  const descriptionAboutArticle = pagesAboutUs?.aboutArticle;
  const aboutUsDocuments =
    pagesAboutUs?.documents?.__typename === 'EntityDocumentConnection'
      ? pagesAboutUs?.documents
      : null;
  const documentsEdges = aboutUsDocuments?.edges ?? [];
  const documents =
    documentsEdges.map((edge) => edge?.node)?.filter((node) => !!node) ?? [];

  const showDocuments =
    documents.length > 0 || (isEditing && !pageContext.isFreePage);
  const showAffiliates = !pageContext.isFreePage || pageContext.isEditor;

  return (
    <div data-testid="ABOUT_US_SUBMODULE">
      <MetaData description={pagesAboutUs?.summary} />
      <Styled.TopContent>
        <DetailedDescription
          articleHeadline={descriptionAboutArticle?.header?.title?.text ?? ''}
          articleContent={descriptionAboutArticle?.content ?? ''}
        />
        {data.facts ? <FactsContainer facts={data.facts} /> : null}
      </Styled.TopContent>
      {pagesAboutUs?.media ? (
        <GalleryPreviewContainer
          media={pagesAboutUs.media}
          mediaItemsNumber={DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS_SUBPAGE}
        />
      ) : null}
      {showDocuments ? <DocumentsContainer documents={documents} /> : null}
      {showAffiliates ? <Affiliates /> : null}
    </div>
  );
};

const LoadingFallback: React.FC = () => {
  return <AboutUsSkeleton />;
};

const ErrorFallback: React.FC<{
  onRetry?: () => void;
}> = ({ onRetry = () => window.location.reload() }) => {
  return <AboutUsErrorPage refetch={onRetry} />;
};

export const App: FC = () => {
  const { pageContext } = usePageContext();
  const [query, { refetch }] = useBackgroundQuery(AboutUsSubpageDocument, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: pageContext.pageSlug,
      first: DEFAULT_MEDIA_AMOUNT_FOR_ABOUTUS_SUBPAGE,
    },
  });

  return (
    <ErrorBoundary onError={() => <ErrorFallback onRetry={refetch} />}>
      <Suspense fallback={<LoadingFallback />}>
        <AppInner query={query} />
      </Suspense>
    </ErrorBoundary>
  );
};
